import { gql } from '@apollo/client';
import useGQLMutation from 'src/lib/graphql/hooks/useGQLMutation';
import useContributionResponseHandler from '../../containers/ContributionResponseHandler/useContributionResponseHandler';
import { UpdateNoteTabResultStatus } from './types';
const UPDATE_NOTE_MUTATION = gql `
  mutation Admin_UpdateNote($data: EPNoteUpdateInput!) {
    ep_updateNote(data: $data) {
      sentForReview
      reviewEntityID
      reason {
        code
        message
        data
      }
    }
  }
`;
const NOTE_SENT_FOR_REVIEW_CONTENT = {
    title: 'Note changes sent for review',
    howItWorksSteps: [
        'MarineSamraj team will review your changes',
        'Once approved, the note will be updated',
        "You'll be notified about the updates",
    ],
};
const NOTE_THANK_CONTRIBUTOR_CONTENT = {
    title: 'Note updated successfully',
};
export function getConflictedNoteTabIds(response) {
    if (response.sentForReview) {
        return [];
    }
    if (response.reason?.code !== 'NOTE_TAB_UPDATE_CONFLICT') {
        return [];
    }
    const tabResults = response.reason.data?.noteTabResults ?? [];
    return tabResults
        .filter((t) => t.status === UpdateNoteTabResultStatus.CONFLICT)
        .map((t) => t.id)
        .filter((id) => id != null && id !== '');
}
export function useUpdateNoteMutation(options) {
    const { update: userUpdate, onCompleted: userOnCompleted, ...rest } = options ?? {};
    const [, contributionHandler] = useContributionResponseHandler({
        sentForReviewContent: NOTE_SENT_FOR_REVIEW_CONTENT,
        thankContributorContent: NOTE_THANK_CONTRIBUTOR_CONTENT,
    });
    return useGQLMutation(UPDATE_NOTE_MUTATION, {
        ...rest,
        update(cache, result, apolloUpdateOpts) {
            userUpdate?.(cache, result, apolloUpdateOpts);
            const data = result.data;
            const { variables } = apolloUpdateOpts;
            if (data?.ep_updateNote && variables) {
                if (data.ep_updateNote.sentForReview) {
                    return;
                }
                const notePatch = variables.data.data;
                const noteId = variables.data.id;
                if (notePatch &&
                    (notePatch.title !== undefined || notePatch.topics !== undefined)) {
                    cache.modify({
                        id: cache.identify({
                            __typename: 'EPNote',
                            id: noteId,
                        }),
                        fields: {
                            data(existing) {
                                const base = existing &&
                                    typeof existing === 'object' &&
                                    existing !== null &&
                                    !('__ref' in existing)
                                    ? { ...existing }
                                    : {};
                                return {
                                    ...base,
                                    ...(notePatch.title !== undefined && { title: notePatch.title }),
                                    ...(notePatch.topics !== undefined && { topics: notePatch.topics }),
                                };
                            },
                        },
                    });
                }
            }
        },
        onCompleted(response) {
            userOnCompleted?.(response);
            const payload = response?.ep_updateNote;
            if (!payload) {
                return;
            }
            const conflictedNoteTabIDs = getConflictedNoteTabIds(payload);
            if (conflictedNoteTabIDs.length > 0) {
                return;
            }
            contributionHandler.handle(payload).catch(() => {
                // Rejection drawers surface the reason; swallow to keep callers simple.
            });
        },
    });
}
